import React from 'react';

/**
 * Renders a mini SVG preview of a custom layout by scaling all boxes down
 * to a 120×60 viewBox. Text-variant boxes render as text-only ticks so the
 * thumbnail visually distinguishes headline blocks from panel cards.
 */
const CustomLayoutThumb = ({ layout }) => {
  if (!layout || !Array.isArray(layout.boxes)) return null;
  const canvasW = layout.canvas_width || 1200;
  const canvasH = layout.canvas_height || 600;
  // Target viewBox — match the picker's aspect ratio (2:1)
  const targetW = 120;
  const targetH = 60;
  const sx = targetW / canvasW;
  const sy = targetH / canvasH;

  return (
    <svg
      viewBox={`0 0 ${targetW} ${targetH}`}
      className="w-full h-full"
      preserveAspectRatio="xMidYMid meet"
      data-testid="custom-layout-thumb"
    >
      {/* Section background */}
      <rect width={targetW} height={targetH} fill="#f8fafc" />

      {/* Small section-title bar hint at the top */}
      {(layout.section_title || layout.eyebrow) && (
        <>
          {layout.eyebrow && (
            <rect x={targetW * 0.35} y={2} width={targetW * 0.3} height={1.2} rx={0.5} fill={layout.accent_color || '#d4af37'} />
          )}
          {layout.section_title && (
            <rect x={targetW * 0.25} y={5} width={targetW * 0.5} height={2.5} rx={1} fill="#0a1a2f" />
          )}
        </>
      )}

      {/* Boxes — sorted by z-index so overlaps stack correctly */}
      {[...layout.boxes]
        .sort((a, b) => (a.z || 0) - (b.z || 0))
        .map((b) => {
          const x = (b.x || 0) * sx;
          const y = (b.y || 0) * sy;
          const w = Math.max(3, (b.w || 100) * sx);
          const h = Math.max(3, (b.h || 60) * sy);
          const cx = x + w / 2;
          const cy = y + h / 2;
          const rot = b.rotation || 0;
          const isText = b.variant === 'text';
          const bg = isText ? 'transparent' : (b.bg_color || '#ffffff');
          const accent = b.accent_color || layout.accent_color || '#d4af37';

          return (
            <g key={b.id} transform={`rotate(${rot} ${cx} ${cy})`}>
              {!isText && (
                <rect
                  x={x}
                  y={y}
                  width={w}
                  height={h}
                  rx={Math.min(w / 2, (b.border_radius ?? 12) * sx * 3)}
                  fill={bg}
                  stroke={bg === '#ffffff' ? '#e2e8f0' : 'none'}
                  strokeWidth={0.3}
                />
              )}
              {/* Icon dot */}
              {!isText && b.show_icon !== false && (
                <rect
                  x={x + 1.2}
                  y={y + 1.2}
                  width={Math.min(4, w / 3)}
                  height={Math.min(4, h / 3)}
                  rx={0.8}
                  fill={`${accent}66`}
                />
              )}
              {/* Title bar */}
              {b.title && (
                <rect
                  x={isText ? x : x + 1.2}
                  y={isText ? y + h / 2 - 1 : y + Math.min(5, h / 2)}
                  width={Math.max(3, w - 2.4)}
                  height={isText ? Math.min(3, h / 2) : 1.6}
                  rx={0.5}
                  fill={b.text_color || '#0a1a2f'}
                  opacity={isText ? 1 : 0.9}
                />
              )}
              {/* Description bar */}
              {b.description && !isText && h > 8 && (
                <rect
                  x={x + 1.2}
                  y={y + Math.min(8, h / 2 + 2)}
                  width={Math.max(2, (w - 3) * 0.8)}
                  height={1}
                  rx={0.5}
                  fill="#94a3b8"
                />
              )}
            </g>
          );
        })}
    </svg>
  );
};

export default CustomLayoutThumb;
